import React from "react";
import { useNavigate } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useMutation, useQuery } from "@apollo/client";

import { Table, PageHeader, Card, Button } from "@cofano/cck";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { TableId, useFrontendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";
import Helmet from "components/helmet/Helmet";

import { DataFormat, Language, usePermission } from "imports/Utils";

import { GetVatCodesDocument, GetVatCodesQuery } from "__generated__/view/vatcodes/graphql/vatCode.queries.generated";
import { DeleteVatCodeDocument } from "__generated__/view/vatcodes/graphql/vatCode.mutations.generated";
import { ChildPropsFromArray } from "types/commons";

type VatCodeFromQuery = ChildPropsFromArray<GetVatCodesQuery, "vatCodes">

const VatCodes = () => {

	const { isReadOnly } = usePermission();
	const confirm = useConfirm();
	const navigate = useNavigate();

	const [ deleteVatCode ] = useMutation(DeleteVatCodeDocument);
	const { data, refetch, loading } = useQuery(GetVatCodesDocument);

	const { tableProps, refetchAfterDelete } = useFrontendTable({
		id: TableId.VatCodes,
		content: data?.vatCodes?.map((vatCode) => ({
			...vatCode,
			type: intl.formatMessage({ id: vatCode.type }),
			transactionType: intl.formatMessage({ id: vatCode.transactionType }),
			allocation: intl.formatMessage({ id: `ALLOCATION_${vatCode.allocation}` }),
		})) ?? [],
		paginationEnabled: false,
	});

	const onDeleteVatCode = (id: number) => {
		deleteVatCode({
			variables: {
				id: id,
			},
		}).then(() => {
			refetchAfterDelete();
			refetch();
		});
	};

	return (
		<React.Fragment>
			<PageHeader>
				<PageHeader.Title>
					{Language("VAT_CODES")}
					<Helmet title={intl.formatMessage({ id: "VAT_CODES" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{!isReadOnly && (
						<Button onClick={() => navigate("/view/vatcodes/create")}>
							{intl.formatMessage({ id: "ADD_BUTTON" })}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			<Card disablePadding>
				<Table
					{...tableProps}
					contentLoading={loading}
					columns={[ {
						id: "code",
						title: intl.formatMessage({ id: "CODE" }),
						sortable: true,
					}, {
						id: "description",
						title: intl.formatMessage({ id: "DESCRIPTION" }),
						sortable: true,
					}, {
						id: "percentage",
						title: intl.formatMessage({ id: "PERCENTAGE" }),
						sortable: true,
						render: (row: VatCodeFromQuery) => DataFormat.percentage(row.percentage),
					}, {
						id: "type",
						title: intl.formatMessage({ id: "TYPE" }),
						sortable: true,
					}, {
						id: "transactionType",
						title: intl.formatMessage({ id: "TRANSACTION_TYPE" }),
						sortable: true,
					}, {
						id: "allocation",
						title: intl.formatMessage({ id: "ALLOCATION" }),
						sortable: true,
					} ]}
					rowActions={isReadOnly ? undefined : [ {
						icon: <EditIcon />,
						onClick: (row: VatCodeFromQuery) => navigate(`/view/vatcodes/${row.id}/edit`),
					}, {
						icon: <DeleteIcon />,
						onClick: (row: VatCodeFromQuery) => {
							confirm({
								type: "DELETE",
							}).then(() => {
								onDeleteVatCode(row?.id);
							});
						},
					} ]} />
			</Card>
		</React.Fragment>
	);
};

export default VatCodes;
